import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

export default function SearchModal({ onClose, onSelectPost }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const inputRef = useRef(null)
  const overlayRef = useRef(null)

  useEffect(() => {
    inputRef.current?.focus()
    const handler = e => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', handler)
    document.body.style.overflow = 'hidden'
    return () => { window.removeEventListener('keydown', handler); document.body.style.overflow = '' }
  }, [onClose])

  useEffect(() => {
    if (!query.trim()) { setResults([]); return }
    const timer = setTimeout(async () => {
      setLoading(true)
      const { data } = await supabase
        .from('posts')
        .select('id, title, excerpt, section, tag, profiles!posts_author_id_fkey(display_name)')
        .eq('status', 'published')
        .or(`title.ilike.%${query}%,excerpt.ilike.%${query}%,tag.ilike.%${query}%`)
        .limit(8)
      setResults(data || [])
      setLoading(false)
    }, 300)
    return () => clearTimeout(timer)
  }, [query])

  return (
    <div ref={overlayRef} onClick={e => e.target === overlayRef.current && onClose()} className="search-modal-overlay">
      <div className="search-modal">
        <div style={styles.inputWrap}>
          <span style={styles.searchIcon}>⌕</span>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search The Margins..."
            style={styles.input}
          />
          {loading && <span style={styles.spinner}>·</span>}
          <button onClick={onClose} style={styles.closeBtn}>Esc</button>
        </div>

        {results.length > 0 && (
          <div style={styles.results}>
            {results.map(r => (
              <button key={r.id} style={styles.result} onClick={() => { onSelectPost(r.id); onClose() }}>
                <div style={styles.resultTag}>{r.tag || r.section}</div>
                <div style={styles.resultTitle}>{r.title}</div>
                {r.excerpt && <div style={styles.resultExcerpt}>{r.excerpt.slice(0, 100)}…</div>}
                <div style={styles.resultAuthor}>{r.profiles?.display_name}</div>
              </button>
            ))}
          </div>
        )}

        {query && !loading && results.length === 0 && (
          <div style={styles.empty}>
            <p style={styles.emptyText}>Nothing found for "{query}"</p>
          </div>
        )}

        {!query && (
          <div style={styles.hint}>
            <p style={styles.hintText}>Search titles, excerpts, tags</p>
          </div>
        )}
      </div>
    </div>
  )
}

const styles = {
  overlay: {
    position: 'fixed', inset: 0, zIndex: 3000,
    background: 'rgba(15,14,12,0.8)', backdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    paddingTop: '12vh', padding: '12vh 1.5rem 0',
    animation: 'fadeIn 0.15s ease',
  },
  modal: {
    background: 'var(--paper)', width: 'min(90vw, 620px)',
    border: '1px solid var(--rule)', boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
    animation: 'modalSlide 0.2s cubic-bezier(0.4,0,0.2,1)',
    maxHeight: '70vh', display: 'flex', flexDirection: 'column',
  },
  inputWrap: {
    display: 'flex', alignItems: 'center', gap: '0.75rem',
    padding: '1rem 1.25rem', borderBottom: '1px solid var(--rule)',
    flexShrink: 0,
  },
  searchIcon: { fontSize: '1.3rem', color: 'var(--muted)', lineHeight: 1 },
  input: {
    flex: 1, border: 'none', outline: 'none', background: 'transparent',
    fontSize: '1.05rem', color: 'var(--ink)', fontFamily: "'DM Sans', sans-serif",
  },
  spinner: { color: 'var(--accent)', fontSize: '1.5rem', animation: 'pulse 1s infinite' },
  closeBtn: {
    background: 'none', border: '1px solid var(--rule)', padding: '0.2rem 0.5rem',
    fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: 'var(--muted)',
    cursor: 'pointer', letterSpacing: '0.06em',
  },
  results: { overflowY: 'auto', flex: 1 },
  result: {
    width: '100%', textAlign: 'left', background: 'none', border: 'none',
    borderBottom: '1px solid var(--rule)', padding: '1rem 1.25rem',
    cursor: 'pointer', transition: 'background 0.15s', display: 'block',
  },
  resultTag: {
    fontFamily: "'DM Mono', monospace", fontSize: '0.58rem',
    letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.3rem',
  },
  resultTitle: {
    fontFamily: "'Space Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700,
    color: 'var(--ink)', marginBottom: '0.25rem',
  },
  resultExcerpt: { fontSize: '0.82rem', color: 'var(--muted)', lineHeight: 1.5, marginBottom: '0.4rem' },
  resultAuthor: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', color: 'var(--muted)' },
  empty: { padding: '2.5rem', textAlign: 'center' },
  emptyText: { fontFamily: "'DM Mono', monospace", fontSize: '0.72rem', color: 'var(--muted)' },
  hint: { padding: '2rem', textAlign: 'center' },
  hintText: { fontFamily: "'DM Mono', monospace", fontSize: '0.68rem', color: 'var(--muted)', letterSpacing: '0.06em' },
}
